import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Building2,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  Circle,
  FileText,
  Phone,
  Mail,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  completed: { label: 'Completed', color: 'bg-green-500' },
  active: { label: 'In Progress', color: 'bg-blue-500' },
  pursuing: { label: 'Planning', color: 'bg-yellow-500' },
  bid_submitted: { label: 'Proposal Sent', color: 'bg-purple-500' }
};

export const SharePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePhase, setActivePhase] = useState(0);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/api/share/project/${token}`);
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This project link is invalid or has been deactivated.');
        } else if (err.response?.status === 410) {
          setError('This project link has expired. Please contact us for an updated link.');
        } else {
          setError('Unable to load project. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#C6A87C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading your project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Unavailable</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a href="tel:9048636673" className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3">
            <Phone className="w-4 h-4" />
            Contact Us
          </a>
        </div>
      </div>
    );
  }

  const { project, phases, documents, company } = data;
  const statusInfo = statusConfig[project.status] || statusConfig.pursuing;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="share-page">
      <header className="bg-[#1a1a1a] text-white py-6">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-[#C6A87C]" />
            <div>
              <h1 className="font-bold text-lg">{company.name}</h1>
              <p className="text-gray-400 text-sm">Project Status Portal</p>
            </div>
          </div>
          <a href={`tel:${company.phone}`} className="text-[#C6A87C] flex items-center gap-1">
            <Phone className="w-4 h-4" />
            {company.phone}
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{project.project_type}</h2>
              <div className="flex items-center gap-2 text-gray-500">
                <MapPin className="w-4 h-4" />
                <span>{project.address}, {project.city}</span>
              </div>
            </div>
            <span className={`inline-flex items-center px-4 py-2 ${statusInfo.color} text-white text-sm font-semibold h-fit`}>
              {statusInfo.label}
            </span>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">Overall Progress</span>
              <span className="text-sm font-bold text-[#C6A87C]">{project.progress_percentage}%</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: `${project.progress_percentage}%` }} transition={{ duration: 1 }} className="h-full bg-gradient-to-r from-[#C6A87C] to-[#B09265]" />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50">
              <p className="text-xs text-gray-500 uppercase mb-1">Client</p>
              <p className="font-medium">{project.client_name}</p>
            </div>
            {project.start_date && (
              <div className="p-4 bg-gray-50">
                <p className="text-xs text-gray-500 uppercase mb-1">Start Date</p>
                <p className="font-medium flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {new Date(project.start_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {project.estimated_completion && (
              <div className="p-4 bg-gray-50">
                <p className="text-xs text-gray-500 uppercase mb-1">Est. Completion</p>
                <p className="font-medium flex items-center gap-1">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(project.estimated_completion).toLocaleDateString()}
                </p>
              </div>
            )}
            <div className="p-4 bg-gray-50">
              <p className="text-xs text-gray-500 uppercase mb-1">Phases</p>
              <p className="font-medium">{phases.length} phases</p>
            </div>
          </div>

          {project.description && (
            <div className="mt-4 p-4 bg-gray-50">
              <p className="text-xs text-gray-500 uppercase mb-1">Project Description</p>
              <p className="text-gray-700">{project.description}</p>
            </div>
          )}
        </motion.div>

        {phases.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Project Phases</h3>
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {phases.map((phase, idx) => (
                <button key={idx} onClick={() => setActivePhase(idx)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${activePhase === idx ? 'bg-[#C6A87C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {phase.name}
                </button>
              ))}
            </div>
            <div className="border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold">{phases[activePhase]?.name}</h4>
                <span className={`text-sm ${phases[activePhase]?.status === 'completed' ? 'text-green-600' : phases[activePhase]?.status === 'in_progress' ? 'text-blue-600' : 'text-gray-500'}`}>
                  {phases[activePhase]?.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                </span>
              </div>
              <div className="space-y-3">
                {phases[activePhase]?.tasks?.length > 0 ? (
                  phases[activePhase].tasks.map((task, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50">
                      {task.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                      )}
                      <p className={`font-medium ${task.status === 'completed' ? 'text-gray-500 line-through' : ''}`}>{task.name}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-center py-4">No tasks in this phase yet</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {documents.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Shared Documents</h3>
            <div className="space-y-2">
              {documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#C6A87C]" />
                    <div>
                      <p className="font-medium">{doc.name}</p>
                      <p className="text-sm text-gray-500">{doc.type.replace('_', ' ').toUpperCase()}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-400">{new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-[#1a1a1a] text-white p-6">
          <h3 className="text-lg font-bold mb-4">Questions About Your Project?</h3>
          <p className="text-gray-400 mb-4">Our team is here to help. Reach out anytime.</p>
          <div className="flex flex-col md:flex-row gap-4">
            <a href={`tel:${company.phone}`} className="inline-flex items-center justify-center gap-2 bg-[#C6A87C] text-white px-6 py-3 hover:bg-[#B09265] transition-colors">
              <Phone className="w-4 h-4" />
              Call Us
            </a>
            <a href={`mailto:${company.email}`} className="inline-flex items-center justify-center gap-2 border border-[#C6A87C] text-[#C6A87C] px-6 py-3 hover:bg-[#C6A87C]/10 transition-colors">
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </div>
        </motion.div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-5xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} {company.name}. All rights reserved.</p>
          <p className="mt-1">This is a private project status page. Please do not share this link.</p>
        </div>
      </footer>
    </div>
  );
};
